import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Search } from 'lucide-react';
import { getCurrentPosition, getLocationName } from '../utils/weather';
import { popularCities, cityRegions } from '../utils/cities';

interface WeatherFormProps {
  onSubmit: (location: string, coordinates: { lat: number; lon: number }) => void;
  isLoading: boolean;
}

export const WeatherForm: React.FC<WeatherFormProps> = ({ onSubmit, isLoading }) => {
  const [location, setLocation] = useState('');
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!location.trim()) return;
    
    const city = popularCities.find(city => city.name === location);
    if (city) {
      onSubmit(city.name, { lat: city.lat, lon: city.lon });
      setLocation('');
      setIsSearchFocused(false);
    }
  };

  const handleGetLocation = async () => {
    try {
      setLocationError(null);
      const position = await getCurrentPosition();
      const coords = {
        lat: position.coords.latitude,
        lon: position.coords.longitude
      };
      
      const locationName = await getLocationName(coords.lat, coords.lon);
      onSubmit(locationName, coords);
    } catch (error) {
      setLocationError(error instanceof Error ? error.message : 'שגיאה באיתור המיקום');
    }
  };

  const handleCitySelect = (city: typeof popularCities[0]) => {
    onSubmit(city.name, { lat: city.lat, lon: city.lon });
    setLocation('');
    setIsSearchFocused(false);
    setSelectedRegion(null);
  };

  const filteredCities = popularCities.filter(city => 
    city.name.includes(location) || !location
  );

  const groupedCities = Object.entries(cityRegions).map(([region, cities]) => ({
    region,
    cities: filteredCities.filter(city => cities.includes(city.name))
  })).filter(group => group.cities.length > 0);

  return (
    <div className="space-y-4">
      <div ref={searchRef} className="relative">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              placeholder="חפש עיר..."
              className="w-full px-4 py-3 pr-10 rounded-lg text-lg bg-slate-50 border-none focus:ring-2 focus:ring-emerald-200 transition-shadow"
            />
            <Search 
              size={20} 
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
          </div>
        </form>

        {isSearchFocused && (
          <div className="absolute z-20 w-full mt-1 bg-white rounded-lg shadow-lg border border-slate-100">
            <div className="max-h-96 overflow-y-auto">
              {groupedCities.map(({ region, cities }) => (
                <div key={region}>
                  <div 
                    className="sticky top-0 p-2 text-sm bg-slate-50 text-slate-600 font-medium border-b border-slate-100"
                    onClick={() => setSelectedRegion(selectedRegion === region ? null : region)}
                  >
                    {region} ({cities.length})
                  </div>
                  <div className={`${selectedRegion && selectedRegion !== region ? 'hidden' : ''}`}>
                    {cities.map((city) => (
                      <button
                        key={city.name}
                        onClick={() => handleCitySelect(city)}
                        className="w-full text-right px-4 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between group"
                      >
                        <span>{city.name}</span>
                        <span className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          בחר ▶
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleGetLocation}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50"
      >
        <MapPin size={20} />
        <span>השתמש במיקום הנוכחי שלי</span>
      </button>
      
      {locationError && (
        <p className="text-sm text-red-500 text-center">
          {locationError}
        </p>
      )}
    </div>
  );
};